/**
 * Phase 641 — Session Plan Builder (drag-drop drill composer)
 *
 * LEFT PANE: filterable drill library. Each drill row is
 *   draggable=true and carries the drill id in dataTransfer.
 * RIGHT PANE: the plan being built. Drop target accepts drills;
 *   rows are re-orderable within the plan via drag-and-drop.
 *
 * Uses the native HTML5 drag-drop API so we don't ship a heavy
 * react-dnd dependency. Falls back gracefully on touch devices via
 * explicit "+" buttons on each drill row.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Save, Plus, Trash2, GripVertical, Search, ArrowLeft,
  Clock, Copy, Dumbbell, X, Timer,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  'warm-up':      'bg-amber-100 text-amber-800',
  'skill':        'bg-blue-100 text-blue-800',
  'tactical':     'bg-purple-100 text-purple-800',
  'conditioning': 'bg-red-100 text-red-800',
  'cool-down':    'bg-emerald-100 text-emerald-800',
  'general':      'bg-slate-100 text-slate-800',
};

export default function SessionPlanBuilderPage() {
  const { id: planId } = useParams();
  const navigate = useNavigate();
  const isEdit = planId && planId !== 'new';

  const [drills, setDrills] = useState([]);
  const [drillFilter, setDrillFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sport, setSport] = useState('');
  const [blocks, setBlocks] = useState([]);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Fetch drill library
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/drills');
        setDrills(Array.isArray(r.data) ? r.data : (r.data?.rows || []));
      } catch { /* empty library is a valid state */ }
    })();
  }, []);

  // Fetch existing plan in edit mode
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/session-plans/${planId}`);
        const d = r.data;
        setTitle(d.title || '');
        setDescription(d.description || '');
        setSport(d.sport || '');
        setBlocks(d.blocks || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load plan');
        navigate('/session-plans');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, planId, navigate]);

  const filteredDrills = useMemo(() => {
    const q = drillFilter.trim().toLowerCase();
    return drills.filter(d => {
      if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
      if (q && !((d.title || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [drills, drillFilter, categoryFilter]);

  const totalMin = useMemo(() => blocks.reduce((s, b) => s + (parseInt(b.duration_minutes, 10) || 0), 0), [blocks]);

  // ── drag/drop helpers ─────────────────────────────────────────
  const onDrillDragStart = useCallback((e, drill) => {
    e.dataTransfer.setData('application/x-outplay-drill', JSON.stringify({ id: drill.id, title: drill.title, duration_minutes: drill.duration_minutes || 15 }));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const onBlockDragStart = useCallback((e, idx) => {
    e.dataTransfer.setData('application/x-outplay-block-idx', String(idx));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDropzoneDragOver = useCallback((e, idx) => {
    e.preventDefault();
    setDragOverIndex(idx ?? blocks.length);
  }, [blocks.length]);

  const onDropzoneDrop = useCallback((e, targetIdx) => {
    e.preventDefault();
    const insertAt = targetIdx ?? blocks.length;
    const drillData = e.dataTransfer.getData('application/x-outplay-drill');
    const blockIdxStr = e.dataTransfer.getData('application/x-outplay-block-idx');
    if (drillData) {
      // Drag from library — append a new block
      try {
        const drill = JSON.parse(drillData);
        const newBlock = {
          drill_id: drill.id,
          drill_title: drill.title,
          duration_minutes: parseInt(drill.duration_minutes || 15, 10),
          note: '',
        };
        const next = [...blocks];
        next.splice(insertAt, 0, newBlock);
        setBlocks(next);
      } catch { /* malformed drag payload */ }
    } else if (blockIdxStr) {
      // Reorder within the plan
      const src = parseInt(blockIdxStr, 10);
      if (Number.isNaN(src) || src === insertAt) return;
      const next = [...blocks];
      const [moved] = next.splice(src, 1);
      const adjustedIdx = src < insertAt ? insertAt - 1 : insertAt;
      next.splice(adjustedIdx, 0, moved);
      setBlocks(next);
    }
    setDragOverIndex(null);
  }, [blocks]);

  const addBlockDirect = useCallback((drill) => {
    setBlocks(prev => [...prev, {
      drill_id: drill.id,
      drill_title: drill.title,
      duration_minutes: parseInt(drill.duration_minutes || 15, 10),
      note: '',
    }]);
  }, []);

  const removeBlock = useCallback((idx) => {
    setBlocks(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBlock = useCallback((idx, key, value) => {
    setBlocks(prev => prev.map((b, i) => i === idx ? { ...b, [key]: value } : b));
  }, []);

  const handleSave = useCallback(async () => {
    if (!title.trim()) { toast.error('Give your plan a title'); return; }
    if (blocks.length === 0) { toast.error('Drag at least one drill into your plan'); return; }
    setSaving(true);
    const payload = {
      title, description, sport,
      blocks: blocks.map(b => ({
        drill_id: b.drill_id,
        drill_title: b.drill_title,
        duration_minutes: parseInt(b.duration_minutes || 15, 10),
        note: b.note || '',
      })),
    };
    try {
      if (isEdit) {
        await api.patch(`/session-plans/${planId}`, payload);
        toast.success('Plan updated');
      } else {
        const r = await api.post('/session-plans', payload);
        toast.success('Plan saved');
        navigate(`/session-plans/${r.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [title, description, sport, blocks, isEdit, planId, navigate]);

  const handleDuplicate = useCallback(async () => {
    if (!isEdit) return;
    try {
      const r = await api.post(`/session-plans/${planId}/duplicate`);
      toast.success('Duplicated — opening the copy');
      navigate(`/session-plans/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Duplicate failed');
    }
  }, [isEdit, planId, navigate]);

  const handleDelete = useCallback(async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this session plan? This cannot be undone.')) return;
    try {
      await api.delete(`/session-plans/${planId}`);
      toast.success('Deleted');
      navigate('/session-plans');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  }, [isEdit, planId, navigate]);

  if (loading) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="session-plan-builder">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/session-plans">
          <Button variant="ghost" size="sm" data-testid="back-to-plans-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </Link>
        <h1 className="text-xl font-display font-semibold flex-1">
          {isEdit ? 'Edit session plan' : 'New session plan'}
        </h1>
        {isEdit && (
          <>
            <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-plan-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDelete}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="delete-plan-btn">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        <Button onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save plan
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT — Drill palette */}
        <div className="lg:col-span-4 space-y-2" data-testid="drill-palette">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Dumbbell className="h-4 w-4 text-primary" /> Drill Library
            <span className="text-[10px] font-mono text-slate-400 ml-auto">{drills.length}</span>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <Input value={drillFilter} onChange={e => setDrillFilter(e.target.value)}
                   placeholder="Search drills…" className="pl-7 h-8 text-xs"
                   data-testid="drill-search-input" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="drill-category-filter">
              <SelectValue placeholder="Any category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="warm-up">Warm-up</SelectItem>
              <SelectItem value="skill">Skill</SelectItem>
              <SelectItem value="tactical">Tactical</SelectItem>
              <SelectItem value="conditioning">Conditioning</SelectItem>
              <SelectItem value="cool-down">Cool-down</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {filteredDrills.length === 0 && (
              <p className="text-xs text-slate-400 p-3 text-center border border-dashed rounded">
                {drills.length === 0 ? 'Your drill library is empty. Add drills first.' : 'No drills match the filter.'}
              </p>
            )}
            {filteredDrills.map(drill => (
              <div key={drill.id}
                   draggable
                   onDragStart={(e) => onDrillDragStart(e, drill)}
                   className="p-2 rounded border border-slate-200 bg-white dark:bg-slate-900 cursor-grab active:cursor-grabbing hover:border-primary hover:shadow-sm transition-all"
                   data-testid={`drill-item-${drill.id}`}>
                <div className="flex items-start gap-2">
                  <GripVertical className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{drill.title}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Badge className={`text-[9px] py-0 px-1 ${CATEGORY_COLORS[drill.category] || CATEGORY_COLORS.general}`}>
                        {drill.category || 'general'}
                      </Badge>
                      <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                        <Clock className="h-2.5 w-2.5" /> {drill.duration_minutes || 15}m
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => addBlockDirect(drill)}
                    className="opacity-0 group-hover:opacity-100 text-primary hover:text-primary-foreground hover:bg-primary rounded p-0.5"
                    title="Add to plan"
                    aria-label={`Add ${drill.title}`}
                    data-testid={`add-drill-${drill.id}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Plan builder */}
        <div className="lg:col-span-8 space-y-3" data-testid="plan-canvas">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="plan-title" className="text-xs">Plan title *</Label>
              <Input id="plan-title" value={title} onChange={e => setTitle(e.target.value)}
                     placeholder="e.g. U-14 Football · Warm-up week 3"
                     data-testid="plan-title-input" />
            </div>
            <div>
              <Label htmlFor="plan-sport" className="text-xs">Sport (optional)</Label>
              <Input id="plan-sport" value={sport} onChange={e => setSport(e.target.value)}
                     placeholder="e.g. Football"
                     data-testid="plan-sport-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="plan-desc" className="text-xs">Coaching intent (optional)</Label>
            <Textarea id="plan-desc" value={description} onChange={e => setDescription(e.target.value)}
                      rows={2} placeholder="What should players walk away better at?"
                      data-testid="plan-desc-input" />
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Session flow · {blocks.length} block{blocks.length === 1 ? '' : 's'}
            </p>
            <p className="text-xs font-mono tabular-nums flex items-center gap-1">
              <Timer className="h-3.5 w-3.5 text-primary" />
              <span className="font-bold">{totalMin}m</span> total
            </p>
          </div>

          {/* Drop zone */}
          <div
            className={`rounded-lg border-2 ${blocks.length === 0 ? 'border-dashed' : 'border-transparent'} min-h-[200px] transition-colors ${dragOverIndex === blocks.length ? 'bg-primary/5 border-primary' : ''}`}
            onDragOver={(e) => onDropzoneDragOver(e, blocks.length)}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => onDropzoneDrop(e, blocks.length)}
            data-testid="plan-dropzone">
            {blocks.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                Drag drills from the left, or click <Plus className="inline h-3.5 w-3.5" /> to add.
              </div>
            ) : (
              <div className="space-y-2 p-2">
                {blocks.map((b, idx) => (
                  <div key={idx}
                       draggable
                       onDragStart={(e) => onBlockDragStart(e, idx)}
                       onDragOver={(e) => onDropzoneDragOver(e, idx)}
                       onDrop={(e) => onDropzoneDrop(e, idx)}
                       className={`p-2 rounded-md border bg-white dark:bg-slate-900 flex items-start gap-2 cursor-grab active:cursor-grabbing ${dragOverIndex === idx ? 'border-primary bg-primary/5' : 'border-slate-200'}`}
                       data-testid={`plan-block-${idx}`}>
                    <GripVertical className="h-4 w-4 text-slate-400 mt-2 flex-shrink-0" />
                    <div className="flex-1 space-y-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-slate-400 tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
                        <p className="text-sm font-semibold truncate flex-1">{b.drill_title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-slate-400" />
                          <Input type="number" min={1} max={180}
                                 value={b.duration_minutes}
                                 onChange={e => updateBlock(idx, 'duration_minutes', e.target.value)}
                                 className="h-6 text-xs w-14 tabular-nums"
                                 data-testid={`block-duration-${idx}`} />
                          <span className="text-[10px] text-slate-500">min</span>
                        </div>
                        <Input value={b.note}
                               onChange={e => updateBlock(idx, 'note', e.target.value)}
                               placeholder="Note (e.g. keep intensity high)"
                               className="h-6 text-xs flex-1"
                               data-testid={`block-note-${idx}`} />
                      </div>
                    </div>
                    <button onClick={() => removeBlock(idx)}
                            className="text-slate-400 hover:text-red-500 mt-2 flex-shrink-0"
                            aria-label={`Remove ${b.drill_title}`}
                            data-testid={`remove-block-${idx}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
